import { maybeBool, maybeNum, maybeNumStr, maybeStr, num, numStr, obj, parsedJSON, phoneStr, str, strOrNum, zEnum, } from "@agrigate/ts-utils/lib/zod";
// doamin/internal depends on this, so any struct that need to depend on anything from
// internal (ie. any generated type) should go into ./ext.ts
import { z } from "zod/v4";
import { AnimalHusbandaryType, CountUnit, DurationUnit, FeedItemType, Grade, IrrigationSourceType, OrderType, PayoutOption, Quality, WeightUnit, } from "../enums";
export const AddressAreaDetails = z.object({
    buildingNumber: z.string().nullish(),
    address: z.string().nullish(),
    cluster: z.string().nullish(),
    block: z.string().nullish(),
    ward: z.string().nullish(),
    tehsil: z.string().nullish(),
    postOffice: z.string().nullish(),
    streetName: z.string().nullish(),
});
export const NumBounds = obj({
    min: maybeNum,
    max: maybeNum,
});
export const NumRange = obj({
    start: numStr,
    end: numStr,
});
export const OpenNumRange = obj({
    start: numStr.nullish(),
    end: numStr.nullish(),
});
export const PTFieldAttachment = obj({
    name: str,
    label: maybeStr,
    whenValue: strOrNum.or(strOrNum.array()).nullish(),
    counts: NumBounds.nullish(),
});
export const PTFieldBase = obj({
    name: str,
    label: maybeStr,
    isRequired: maybeBool,
    isTL: maybeBool,
    isInSummary: maybeBool,
    attachments: PTFieldAttachment.array().nullish(),
    orderScope: OrderType.array().nullish(),
});
export const ChoiceItem = (valType) => obj({
    label: maybeStr,
    value: valType,
});
export const PTFieldChoice = (valType) => obj({
    displayType: z.enum(["dropdown", "pills"]).nullish(),
    allowFreeText: maybeBool,
    items: ChoiceItem(valType).array(),
});
export const PTStrField = PTFieldBase.extend({
    type: z.literal("string"),
    lengthBounds: NumBounds.nullish(),
    defaultValue: maybeStr,
    choices: PTFieldChoice(str).nullish(),
});
export const PTNumField = PTFieldBase.extend({
    type: z.enum(["int", "decimal"]),
    valueBounds: NumBounds.nullish(),
    defaultValue: numStr.nullish(),
    choices: PTFieldChoice(num).nullish(),
});
export const PTNumRangeField = PTFieldBase.extend({
    type: z.enum(["intRange", "decimalRange"]),
    startBounds: NumBounds.nullish(),
    endBounds: NumBounds.nullish(),
    defaultValue: OpenNumRange.nullish(),
    choices: PTFieldChoice(OpenNumRange).nullish(),
});
export const PTField = PTStrField.or(PTNumField).or(PTNumRangeField);
export const PTChoiceItem = PTFieldChoice(str)
    .or(PTFieldChoice(num))
    .or(PTFieldChoice(OpenNumRange));
export const ProduceTypeFields = z.object({
    fields: PTField.array().nullish(),
});
export const NumWithUnit = (Unit) => obj({
    input: numStr,
    unit: Unit,
});
export const formatNumWithUnit = (n) => n ? `${n.input} ${n.unit}` : "";
export const DurationInput = NumWithUnit(DurationUnit);
export const WeightInput = NumWithUnit(WeightUnit);
export const CountInput = NumWithUnit(CountUnit);
const QuantityInput = WeightInput.or(CountInput);
const DispatchInfo = z.object({
    docNo: z.string().nullish(),
    deliveryNoteDate: z.string().nullish(),
    dispatchedThrough: z.string().nullish(),
});
const OrderProps = z.looseObject({
    grade: Grade.nullish(),
    quality: Quality.nullish(),
    quantity: QuantityInput.or(QuantityInput.array()),
    fungusContent: OpenNumRange.nullish(),
    moistureContent: numStr.or(OpenNumRange).nullish(),
    availabilityExpectedIn: DurationInput,
    expiresIn: DurationInput,
    images: z.record(z.string(), z.string().array()).nullish(),
    amount: maybeNum,
    dispatch: DispatchInfo.nullish(),
});
export const ProduceBatchProps = OrderProps;
export const ShipmentItemProps = OrderProps.omit({
    availabilityExpectedIn: true,
    expiresIn: true,
    quantity: true,
});
export const CollectionTargetItemProps = z.record(z.string(), obj({
    min: maybeNum,
    max: maybeNum,
}));
export const ShipmentProps = z.looseObject({
    lrNo: z.string().nullish(),
    images: z.record(z.string(), z.string().array()).nullish(),
});
export const UploadDestination = z.object({
    keyPath: z.string(),
    url: z.string(),
});
export const BuyerOrderProps = OrderProps.omit({
    availabilityExpectedIn: true,
});
export const SettlementDeduction = z.object({
    code: z.string(),
    message: z.string().nullish(),
    internalComment: z.string().nullish(),
    percent: numStr.nullish(),
    amount: numStr.nullish(),
    isObfuscated: z.boolean().nullish(),
    zohoLineItemId: maybeStr,
});
export const SettlementDeductions = z.object({
    items: SettlementDeduction.array(),
});
export const EmployeeTrackEntry = z.object({
    lat: z.number(),
    long: z.number(),
    at: z.number(),
});
export const EmployeeTrackEntries = z.object({
    entries: EmployeeTrackEntry.array(),
});
export const EmployeeTrackKeys = z.object({
    keyPaths: str.array(),
});
export const UserKycDocUploadPaths = z.object({
    keyPaths: str.array().nullish(),
});
export const PaymentProof = z.object({
    keyPaths: str.array(),
});
export const AppInstallSourceDetails = obj({
    phoneNumbers: z.string().array().nullish(),
});
export const DemandSpeculationGeoScope = obj({
    districts: z.string().array().nullish(),
});
export const FeedItemSource = obj({
    iconPath: str.nullish(),
    name: str.nullish(),
    link: str.nullish(),
});
export const FeedItemProps = obj({
    type: FeedItemType.nullish(),
    venue: str.nullish(),
    scheduledAt: z.number().nullish(),
    schemeDeadline: z.number().nullish(),
    topics: str.array().nullish(),
    contactPersonId: str.nullish(),
    sources: FeedItemSource.array().nullish(),
    disableCoverTextOverlay: maybeBool,
    genAudio: maybeBool,
    useSameMediaKey: maybeBool,
    whatsappHeaderType: z.enum(["text", "image"]).nullish(),
});
export const FeedSubscriptionCohortConfig = obj({
    location: obj({
        lat: numStr,
        lon: numStr,
    }),
    target_sites: str.array(),
    keywords: obj({
        agriculturalOutputProducts: str.array().nullish(),
        agriculturalEquipments: str.array().nullish(),
        commercialBrands: str.array().nullish(),
        agriculturalInputProducts: str.array().nullish(),
        indianStates: str.array().nullish(),
        indianCities: str.array().nullish(),
        indianDistricts: str.array().nullish(),
        nonIndianPlaces: str.array().nullish(),
    }).nullish(),
});
export const AlertProps = obj({
    highlights: obj({
        text: str,
        color: str,
    }).array(),
});
// Base condition types
export const EqualityCondition = z.object({
    type: z.literal("equality"),
    field: z.string(),
    value: z.any(),
});
export const RangeCondition = z.object({
    type: z.literal("range"),
    field: z.string(),
    min: z.number(),
    max: z.number(),
});
// Recursive types for AND/OR conditions
export const CoinConditionProps = z.lazy(() => z.union([
    EqualityCondition,
    RangeCondition,
    z.object({
        type: z.literal("and"),
        conditions: z.array(CoinConditionProps),
    }),
    z.object({
        type: z.literal("or"),
        conditions: z.array(CoinConditionProps),
    }),
]));
export const PayoutScheduleItem = obj({
    mode: PayoutOption,
    /** Adjustment in the total for using this specific payout mode */
    adjustment: numStr.nullish(),
    /** Final amount to be paid through this mode */
    amount: numStr,
    settlementDays: maybeNum,
    immediateSettlement: maybeBool,
});
export const PayoutSchedule = obj({
    payouts: PayoutScheduleItem.array().nullish(),
});
export const NotificationDeliveryMode = z.enum(["WHATSAPP", "FCM", "SMS"]);
export const NotificationBatchContext = obj({
    subject: obj({
        targetId: maybeStr,
        targetItemId: maybeStr,
        feedItemId: maybeStr,
        buyerOrderId: maybeStr,
        buyerSettlementId: maybeStr,
        produceBatchId: maybeStr,
        produceBatchSettlementId: maybeStr,
    }).nullish(),
    recipientIds: z.string().array(),
    modes: NotificationDeliveryMode.array(),
});
const Delta = obj({
    role: zEnum(["user", "system", "assistant"]),
    content: z.string(),
});
const Usage = obj({
    prompt_tokens: z.number(),
    completion_tokens: z.number(),
    total_tokens: z.number(),
    search_context_size: z.string(),
});
const Choice = obj({
    index: z.number(),
    finish_reason: z.string(),
    message: Delta,
    delta: Delta,
});
export const PerplexityResponse = obj({
    id: z.string(),
    model: z.string(),
    created: z.number(),
    usage: Usage,
    citations: z.array(z.string()),
    object: z.string(),
    choices: z.array(Choice),
});
export const AIMessageResponse = z.discriminatedUnion("role", [
    z.object({
        role: z.literal("assistant"),
        id: z.string(),
        model: z.string(),
        created: z.number(),
        usage: Usage,
        citations: z.array(z.string()),
        object: z.string(),
        choices: z.array(Choice),
    }),
    z.object({
        role: z.enum(["user", "system"]),
        content: z.string(),
    }),
]);
export const FeedInteractionCounts = z.record(z.string(), z.number());
export const ContentBlock = obj({
    key: maybeStr,
    title: str,
    description: maybeStr,
    seq: maybeNum,
    iconPath: maybeStr,
    showOnCard: maybeBool,
});
export const ContentList = parsedJSON.pipe(obj({
    items: ContentBlock.array(),
}).nullish());
export const FeedItemVideoProps = obj({
    primaryUrl: str.nullish(),
    fallbackUrl: str,
    streamJobStatus: z.enum(["PENDING", "COMPLETE", "ERROR"]).nullish(),
    error: str.nullish(),
}).array();
export const FeedItemImageProps = obj({
    url: str,
}).array();
export const FeedExternalLink = obj({
    url: str,
});
export const FeedExternalLinks = FeedExternalLink.array();
export const LocationIds = obj({
    regionId: maybeStr,
    districtId: maybeStr,
    blockId: maybeStr,
    clusterId: maybeStr,
});
export const ShipmentCosts = obj({
    handlingCharge: maybeNumStr,
    transportationCharge: maybeNumStr,
    advanceAmount: maybeNumStr,
});
export const ShipmentContact = obj({
    fullName: str.nonempty(),
    phoneNumber: phoneStr.or(phoneStr.array()).nullish(),
    email: str.or(str.array()).nullish(),
}).refine(val => {
    return !![val.phoneNumber, val.email].find(contact => (typeof contact === "string" && contact.length > 0) ||
        (Array.isArray(contact) &&
            contact.length > 0 &&
            contact.every(item => item.length > 0)));
}, {
    error: "Non-empty phone or email must be passed",
});
export const QualityFieldItem = obj({
    itemId: str,
    fieldName: str,
    value: numStr,
});
export const EquipmentModelAssetType = z.enum(["image", "video", "document"]);
// Equipment Model Assets type
export const EquipmentModelAssets = z.partialRecord(EquipmentModelAssetType, obj({
    keyPath: z.string(),
    isCover: maybeBool,
})
    .array()
    .nullish());
export const ProduceTypeFieldOrderScope = z.object({
    scopes: OrderType.array(),
});
export const PTFieldAttachments = PTFieldAttachment.array();
export const PTFieldConfig = z.object({
    defaultValue: str.or(numStr).or(OpenNumRange).nullish(),
    choices: PTChoiceItem.nullish(),
    lengthBounds: NumBounds.nullish(),
    valueBounds: NumBounds.nullish(),
    startBounds: NumBounds.nullish(),
    endBounds: NumBounds.nullish(),
});
export const CropSelection = z.object({
    produceTypeKey: z.string().nullish(),
    produceTypeName: z.string().nullish(),
    season: z.string().array().nullish(),
    variantKeys: z.string().array().nullish(),
});
export const CropProductionProfile = z.object({
    isSkipped: maybeBool,
    crops: CropSelection.array().nullish(),
});
export const LandHoldingProfile = z.object({
    isSkipped: maybeBool,
    pinCode: z.string().nullish(),
    areaOriginal: z
        .object({
        unit: str,
        from: z.number().nullish(),
        to: z.number().nullish(),
    })
        .nullish(),
    areaAcres: z
        .object({
        from: z.number().nullish(),
        to: z.number().nullish(),
    })
        .nullish(),
});
export const LiveStockProfile = z.object({
    isSkipped: maybeBool,
    ownLiveStock: z.boolean().nullish(),
    animalHusbandryType: AnimalHusbandaryType.array().nullish(),
});
export const IrrigationProfile = z.object({
    isSkipped: maybeBool,
    types: IrrigationSourceType.array().nullish(),
});
export const FarmerAgriProfileDetails = z.object({
    producedCrops: CropProductionProfile.or(
    // Temporary workaround to support old version of app
    CropSelection.array().transform(_ => ({ crops: _ }))).nullish(),
    landHolding: LandHoldingProfile.nullish(),
    irrigation: IrrigationProfile.nullish(),
    livestock: LiveStockProfile.nullish(),
});
//# sourceMappingURL=base.js.map